import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, ModeComment } from '@mui/icons-material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  CommentModel,
  deleteComment,
  listComments,
  postComment,
} from '../api/blogApi';
import { useGoogleAuth } from '../auth/useGoogleAuth';
import { MAX_COMMENT_CHARS } from '../config/site';

/** "3 days ago" style stamp, falling back to a date once it stops being useful. */
const relativeTime = (ms: number): string => {
  const seconds = Math.round((Date.now() - ms) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
  ];

  const formatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  let value = seconds;
  for (const [unit, step] of units) {
    if (Math.abs(value) < step) return formatter.format(-Math.round(value), unit);
    value /= step;
  }
  return new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

type CommentsProps = {
  blogId: string;
};

const Comments: React.FC<CommentsProps> = ({ blogId }) => {
  const auth = useGoogleAuth();
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const { comments: loaded } = await listComments(blogId, auth.token ?? undefined);
      setComments(loaded);
    } catch {
      setError("Couldn't load comments.");
    } finally {
      setLoading(false);
    }
  }, [blogId, auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  // The Google button is painted imperatively, so it has to be (re)rendered
  // whenever the container appears or the script finishes loading.
  useEffect(() => {
    if (!auth.user) auth.renderButton(buttonRef.current);
  }, [auth, auth.user, auth.ready]);

  const tooLong = draft.length > MAX_COMMENT_CHARS;
  const canPost = draft.trim() !== '' && !tooLong && !posting && !!auth.token;

  const submit = async () => {
    if (!canPost || !auth.token) return;
    setPosting(true);
    setError(null);
    try {
      const created = await postComment(blogId, draft.trim(), auth.token);
      setComments((prev) => [...prev, created]);
      setDraft('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        auth.invalidate();
        setError('Your sign-in expired. Please sign in again.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not post your comment.');
      }
    } finally {
      setPosting(false);
    }
  };

  const remove = async (comment: CommentModel) => {
    if (!auth.token) return;
    try {
      await deleteComment(blogId, comment.id, auth.token);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that comment.');
    }
  };

  return (
    <Paper elevation={6} sx={{ p: { xs: 2, sm: 4 }, mt: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <ModeComment color="primary" />
        <Typography variant="h5" component="h2">
          Comments{!loading && comments.length > 0 ? ` (${comments.length})` : ''}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[0, 1].map((i) => (
            <Stack direction="row" spacing={2} key={i}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="85%" />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No comments yet. Be the first to say something.
        </Typography>
      ) : (
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {comments.map((comment) => (
            <Stack direction="row" spacing={2} key={comment.id} alignItems="flex-start">
              <Avatar src={comment.picture} alt={comment.name} sx={{ width: 40, height: 40 }}>
                {comment.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2">{comment.name}</Typography>
                  {comment.isAuthor && (
                    <Chip label="Author" size="small" color="primary" sx={{ height: 20 }} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {relativeTime(comment.created)}
                  </Typography>
                </Stack>
                {/* Rendered as plain text. React escapes it, so comment
                    bodies can never inject markup into the page. */}
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {comment.body}
                </Typography>
              </Box>
              {comment.mine && (
                <Tooltip title="Delete comment">
                  <IconButton size="small" onClick={() => remove(comment)} aria-label="Delete comment">
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      {!auth.configured ? (
        <Typography variant="body2" color="text.secondary">
          Sign-in isn't configured yet, so commenting is temporarily unavailable.
        </Typography>
      ) : auth.user ? (
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Avatar src={auth.user.picture} alt={auth.user.name} sx={{ width: 32, height: 32 }}>
              {auth.user.name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2">
              Commenting as <strong>{auth.user.name}</strong>
            </Typography>
            <Button size="small" onClick={auth.signOut} sx={{ ml: 'auto' }}>
              Sign out
            </Button>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            error={tooLong}
            helperText={`${draft.length} / ${MAX_COMMENT_CHARS}`}
            slotProps={{ formHelperText: { sx: { textAlign: 'right', m: 0, mt: 0.5 } } }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!canPost}
              startIcon={posting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {posting ? 'Posting…' : 'Post comment'}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Sign in with Google to leave a comment. Only your name and email are stored.
          </Typography>
          <div ref={buttonRef} />
          {auth.error && <Typography variant="caption" color="error">{auth.error}</Typography>}
        </Stack>
      )}
    </Paper>
  );
};

export default Comments;
